/* eslint-disable no-plusplus */
import {
  CircleTickIcon,
  RadioCheckedIcon,
  RadioUncheckedIcon,
} from '@commenda/icons';
import { classNames, cn } from '@commenda/utils';
import { TTimelineItem, TTimelineItemStatus } from './types';

function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}

export type TimelineProps = {
  steps: TTimelineItem[];
  setActiveStep: (itemIndex: number) => void;
  hideTail?: boolean;
  forceDisable?: boolean;
};

export type TimelineItemProps = {
  item: TTimelineItem;
  lastFilledStepIndex: number;
  setActiveStep: (itemIndex: number) => void;
  index: number;
  hideTail?: boolean;
  forceDisable?: boolean;
};

const TimelineItem = ({
  item,
  hideTail = false,
  setActiveStep,
  index,
  lastFilledStepIndex,
  forceDisable = false,
}: TimelineItemProps) => {
  const { status, text, isCompleted, disabled } = item;

  const icon =
    status === TTimelineItemStatus.COMPLETED || isCompleted ? (
      <CircleTickIcon className="text-good-to-go-100" />
    ) : status === TTimelineItemStatus.ACTIVE ? (
      <RadioCheckedIcon />
    ) : (
      <RadioUncheckedIcon />
    );

  const tailColor =
    status === TTimelineItemStatus.COMPLETED || isCompleted
      ? 'border-fg-success-hover'
      : '';

  return (
    <div
      className={classNames(
        'flex gap-3',
        index <= lastFilledStepIndex + 1 && !disabled && !forceDisable
          ? 'cursor-pointer'
          : 'pointer-events-none'
      )}
      {...(!disabled && !forceDisable
        ? { onClick: () => setActiveStep(index) }
        : {})}
    >
      <div className="flex flex-col items-center">
        {icon}
        {!hideTail && (
          <div className="h-6 flex items-center">
            <div className={cn('h-4 border rounded-md', tailColor)} />
          </div>
        )}
      </div>
      <span className="txt-small-bold text-text-secondary pt-0.5">{text}</span>
    </div>
  );
};

const Timeline = ({
  steps,
  setActiveStep,
  hideTail = false,
  forceDisable = false,
}: TimelineProps) => {
  if (!steps) return null;

  const lastFilledStepIndex = findLastIndex(steps, (step) => step.isCompleted);
  return (
    <div className={classNames('flex flex-col', hideTail ? 'gap-4' : '')}>
      {steps.map((step, idx) =>
        !step.isHidden ? (
          <TimelineItem
            item={step}
            hideTail={hideTail || idx === steps.length - 1}
            key={step.id}
            setActiveStep={setActiveStep}
            index={idx}
            lastFilledStepIndex={lastFilledStepIndex}
            forceDisable={forceDisable}
          />
        ) : null
      )}
    </div>
  );
};

export { Timeline };
